import React from 'react';
import PropTypes from 'prop-types';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Row, UncontrolledDropdown } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { randomExportSizes } from '../../data/content/randomExport';

const composition = randomSet => {
  const parts = [];

  if (randomSet.testletCount) {
    parts.push(`${randomSet.testletCount} ${randomSet.testletCount === 1 ? 'testlet' : 'testlets'}`);
  }

  if (randomSet.standAloneCount) {
    parts.push(`${randomSet.standAloneCount} stand-alone`);
  }

  return parts.join(' + ');
};

/*
 * Why the set is not simply the size that was asked for, or why a re-roll keeps
 * returning it. A testlet is taken whole, so a set can run over. It only comes up
 * short when the results hold fewer questions than were asked for.
 */
const sizeNote = ({ isOnlySet, questionCount, requested }) => {
  if (questionCount < requested) {
    return `only ${questionCount} in these results, ${requested} requested`;
  }

  if (questionCount > requested) {
    return (
      `${questionCount - requested} more than the ${requested} requested, as testlets stay whole` +
      (isOnlySet ? ', and the only set this close in these results' : '')
    );
  }

  return isOnlySet ? `the only set of exactly ${requested} in these results` : '';
};

/*
 * Draw a random set from whatever the search currently matches.
 *
 * It sits with the results rather than with the search controls so it cannot be
 * mistaken for a way of searching, and it reports the real count of the drawn set
 * because a testlet is taken whole and may not add up to the size asked for.
 */
const RandomSetBar = ({ isDisabled, isUserAdmin, matchCount, onClear, onExport, onPick, onReroll, randomSet }) => {
  const countLabel = typeof matchCount === 'number' ? `${matchCount} ${matchCount === 1 ? 'result' : 'results'}` : '';

  return (
    <Row className="align-items-center justify-content-between mx-0 mb-3">
      {randomSet ? (
        <div className="text-700 fs--1 py-1" role="status">
          <FontAwesomeIcon icon="random" className="mr-2 text-primary" />
          <strong>
            {randomSet.questionCount} {randomSet.questionCount === 1 ? 'question' : 'questions'}
          </strong>
          {composition(randomSet) && <span className="text-600">{` · ${composition(randomSet)}`}</span>}
          {sizeNote(randomSet) && <span className="text-600">{` · ${sizeNote(randomSet)}`}</span>}
        </div>
      ) : (
        <div className="text-600 fs--1 py-1" role="status">
          {countLabel}
        </div>
      )}

      {randomSet ? (
        <div>
          {isUserAdmin && (
            <Button color="success" size="sm" className="mr-2" onClick={onExport} disabled={isDisabled}>
              <FontAwesomeIcon icon="file-export" className="mr-2" />
              Export set
            </Button>
          )}
          {/* A disabled button drops the keyboard focus, and this one is pressed again and again, so
              while a draw is loading it stays focusable and ignores the press instead. */}
          <Button
            color="falcon-default"
            size="sm"
            className={'mr-2' + (isDisabled ? ' disabled' : '')}
            onClick={isDisabled ? undefined : onReroll}
            aria-disabled={isDisabled}
          >
            <FontAwesomeIcon icon="redo" className="mr-2" />
            Re-roll
          </Button>
          <Button color="falcon-default" size="sm" onClick={onClear} disabled={isDisabled}>
            Clear
          </Button>
        </div>
      ) : (
        <UncontrolledDropdown>
          <DropdownToggle caret color="falcon-default" size="sm" disabled={isDisabled} data-toggle="dropdown">
            <FontAwesomeIcon icon="random" className="mr-2" />
            Random set
          </DropdownToggle>
          <DropdownMenu right>
            {randomExportSizes.map(size => (
              <DropdownItem key={size} onClick={() => onPick(size)} disabled={matchCount === 0}>
                {size} questions
              </DropdownItem>
            ))}
          </DropdownMenu>
        </UncontrolledDropdown>
      )}
    </Row>
  );
};

RandomSetBar.propTypes = {
  isDisabled: PropTypes.bool,
  isUserAdmin: PropTypes.bool,
  matchCount: PropTypes.number,
  onClear: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  onReroll: PropTypes.func.isRequired,
  randomSet: PropTypes.shape({
    isOnlySet: PropTypes.bool,
    questionCount: PropTypes.number,
    requested: PropTypes.number,
    standAloneCount: PropTypes.number,
    testletCount: PropTypes.number
  })
};

RandomSetBar.defaultProps = {
  isDisabled: false,
  isUserAdmin: false,
  matchCount: null,
  randomSet: null
};

export default RandomSetBar;
